import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useToast } from "@/hooks/use-toast";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import { Button } from "@/components/ui/button";
import { 
  MessageSquare,
  Phone,
  Users,
  Hash,
  Crown,
  AlertTriangle
} from "lucide-react";

// Map limit types to plan field names
const limitFieldMap = {
  sms: 'maxSmsPerMonth' as const,
  voice: 'maxVoiceMinutesPerMonth' as const,
  teamMembers: 'maxUsers' as const,
  phoneNumbers: 'maxPhoneNumbers' as const
};

interface UsageStatus {
  within_limits: boolean;
  limits: {
    smsOutbound: number;
    voiceMinutesOutbound: number;
    teamMembers: number;
    phoneNumbers: number;
    isTrial: boolean;
  };
  usage: {
    sms: number;
    voice: number;
    teamMembers: number;
    phoneNumbers: number;
  };
  percentages: {
    sms: number;
    voice: number;
    teamMembers: number;
    phoneNumbers: number;
  };
  isTrial: boolean;
}

const limitIcons = {
  sms: MessageSquare,
  voice: Phone,
  teamMembers: Users,
  phoneNumbers: Hash
};

const limitLabels = {
  sms: 'SMS messages',
  voice: 'voice minutes',
  teamMembers: 'team members',
  phoneNumbers: 'phone numbers'
};

export function UsageLimitWarnings() {
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const { openUpgradeModal } = useUpgradeModal();
  const [shownWarnings, setShownWarnings] = useState<Set<string>>(new Set());

  // Reset shown warnings when organization changes
  useEffect(() => {
    setShownWarnings(new Set());
  }, [currentOrganization?.id]);

  // Fetch usage status periodically
  const { data: usageStatus } = useQuery<UsageStatus>({
    queryKey: ['/api/usage/status'],
    enabled: !!currentOrganization?.id,
    refetchInterval: 60000, // Check every 60 seconds (server-side cached for 5min)
  });

  // Fetch upgrade context for next-tier plan limits (database-driven)
  const { data: upgradeContext } = useQuery<any>({
    queryKey: ['/api/billing/upgrade-context'],
    staleTime: 60000,
    enabled: !!currentOrganization?.id
  });

  useEffect(() => {
    if (!usageStatus || !upgradeContext) return;

    const { percentages, isTrial, limits, usage } = usageStatus;
    
    // Helper to get next-tier plan limit for a specific type
    const getNextTierLimit = (type: keyof typeof percentages, currentLimit: number) => {
      const fieldName = limitFieldMap[type];
      const nextPlan = upgradeContext.availablePlans?.find((plan: any) => {
        const planLimit = plan[fieldName];
        return !planLimit || planLimit > currentLimit;
      });
      
      if (!nextPlan) return null;
      
      const limitValue = nextPlan[fieldName];
      return {
        plan: nextPlan,
        limit: limitValue,
        display: limitValue ? limitValue.toLocaleString() : 'Unlimited'
      };
    };
    
    const checkLimit = (
      type: keyof typeof percentages,
      percentage: number,
      currentUsage: number,
      limit: number
    ) => {
      const warningKey90 = `${type}-90`;
      const warningKey80 = `${type}-80`;
      const Icon = limitIcons[type];
      const label = limitLabels[type];
      const nextTier = getNextTierLimit(type, limit);

      // 90% warning (urgent) with actual next-tier plan limits
      if (percentage >= 90 && percentage < 100 && !shownWarnings.has(warningKey90)) {
        toast({
          title: "⚠️ 90% Limit Reached",
          description: (
            <div className="space-y-3 pt-2">
              <p>
                You've used {currentUsage} of {limit} {label}.
                {nextTier && ` Upgrade to ${nextTier.plan.name} for ${nextTier.display} ${label}.`}
              </p>
              <Button 
                size="sm" 
                onClick={() => {
                  toast({ title: "" }); // Close toast
                  openUpgradeModal(nextTier?.plan?.id);
                }}
                className="bg-blue-600 hover:from-blue-700 hover:to-blue-700 text-white w-full"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          ),
          variant: "destructive",
          duration: 10000,
        });
        setShownWarnings(prev => new Set(prev).add(warningKey90));
      }
      // 80% warning (caution) with actual plan info
      else if (percentage >= 80 && percentage < 90 && !shownWarnings.has(warningKey80)) {
        const upgradeMessage = nextTier 
          ? `Consider upgrading to ${nextTier.plan.name} for ${nextTier.display} ${label}.`
          : "You're approaching your plan limit.";
          
        toast({
          title: "⚡ 80% Limit Reached",
          description: (
            <div className="space-y-3 pt-2">
              <p>
                You've used {currentUsage} of {limit} {label}. {upgradeMessage}
              </p>
              <Button 
                size="sm"
                variant="outline"
                onClick={() => {
                  toast({ title: "" }); // Close toast
                  openUpgradeModal(nextTier?.plan?.id);
                }}
                className="w-full"
              >
                View Plans
              </Button>
            </div>
          ),
          duration: 8000,
        });
        setShownWarnings(prev => new Set(prev).add(warningKey80));
      }
    };

    // Check all limits
    if (percentages.sms > 0) {
      checkLimit('sms', percentages.sms, usage.sms, limits.smsOutbound);
    }
    if (percentages.voice > 0) {
      checkLimit('voice', percentages.voice, usage.voice, limits.voiceMinutesOutbound);
    }
    if (percentages.teamMembers > 0) {
      checkLimit('teamMembers', percentages.teamMembers, usage.teamMembers, limits.teamMembers);
    }
    if (percentages.phoneNumbers > 0) {
      checkLimit('phoneNumbers', percentages.phoneNumbers, usage.phoneNumbers, limits.phoneNumbers);
    }
  }, [usageStatus, upgradeContext, shownWarnings, toast, openUpgradeModal]);

  // Reset warnings when usage drops below threshold (e.g., new billing period)
  useEffect(() => {
    if (!usageStatus) return;

    const { percentages } = usageStatus;
    const newShownWarnings = new Set(shownWarnings);
    let hasChanges = false;

    // Clear 80% warnings if usage drops below 80%
    Object.entries(percentages).forEach(([type, percentage]) => {
      if (percentage < 80 && shownWarnings.has(`${type}-80`)) {
        newShownWarnings.delete(`${type}-80`);
        hasChanges = true;
      }
      if (percentage < 90 && shownWarnings.has(`${type}-90`)) {
        newShownWarnings.delete(`${type}-90`);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setShownWarnings(newShownWarnings);
    }
  }, [usageStatus, shownWarnings]);

  return null; // This component just manages toasts, no UI
}
